<template>
    <div id="contents" class="contents spirit-of-japan">
        <section class="main">
            <div class="texts">
                <div class="inner">
                    <p class="sub">A Journey to the True Spirit of Japan</p>
                    <h2>“O-sake” — the drink that nurtured Japanese history</h2>
                    <p class="lead">
                        Since ancient times, Japanese people have held a deep respect for water, which has been used down through the centuries to produce various sake (rice wines) reflecting the unique customs of each region.
                        <br>
                        Rice shochu partaken off together to win the hearts of the people, sake offering respite to the samurai warriors who refined military arts, divine sake offered to the gods, and Japan’s largest sake-brewing region quenching the thirst of Edo (present-day Tokyo) citizens.
                        <br>
                        Sake has long-since transcended from a mere “drink” to a cultural treasure that has been an irreplaceable element in weaving Japanese history.
                        <br>
                        This section reveals the stories of various regions of Japan found behind the aromas and flavors of sake.
                    </p>
                </div>
            </div>
            <div class="img"><img src="../../assets/spirit_of_japan7/main01.webp" alt="" class="_pc"><img src="../../assets/spirit_of_japan7/main01.webp" alt="" class="_sp"></div>
        </section>
        <section class="basic-section">
            <div class="para -reverse">
                <div class="img"><img src="../../assets/spirit_of_japan7/img01.webp" alt=""></div>
                <div class="txt">
                    <h3>The Rice Shochu that Strengthened the Bond Between the Rulers and the People</h3>
                    <p>
                        Located in Kyushu’s Kumamoto Prefecture, the region of Hitoyoshi Kuma boasts an unusual history.
                        From the birth of samurai society in the Kamakura Period to the dawn of modern Japan in the Meiji Restoration, Hitoyoshi Kuma was ruled by one family for 700 years: the Sagaras.
                        This exceptionally-long reign was made possible by the bond shared between the Sagaras and their subjects, famously through the local liquor “Kuma Shochu.” Because the Sagaras were originally from far-away Totomi (in present-day Shizuoka prefecture), they made a conscious effort to respect local traditions and beliefs.
                        Alongside their more conservative policies, the Sagaras Established irrigation systems and stimulated the local economy through maritime trade. As a symbolic example, the locals were granted the right to use rice to brew shochu.
                        At the time, it was a precious commodity reserved for annual tax tribute.
                        This combination of conservate and progressive policy, as well as the Sagaras’ leniency towards the public, together with local expertise, led to the growth of the region’s rich culture.
                        This harmony continued down for generations, ultimately blossoming into 700 years of into what came to be known as Sagara Culture.
                        Even today, visitors may experience regional specialties such as the highly aromatic Kuma Shochu, vibrant festivals, and other traditional events.
                    </p>
                    <div class="btns">
                        <ul>
                            <li>
                                <router-link :to="{name: 'Popular', params: {id: '517f27e1-547a-4dc1-a082-bba823049c73', page: 'about', url: 'mountain-village-culture-nurtured-kumamoto-hitoyoshi-kuma '}}" class="-black">
                                    <span>Read More</span>
                                </router-link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>
        <section class="basic-section">
            <div class="para">
                <div class="txt">
                    <h3>Satsuma Shochu – A Samurai’s Remedy for Fatigue</h3>
                    <p>
                        The domain of Satsuma which governed present-day Kagoshima prefecture and parts of neighboring Miyazaki prefecture in the Edo period (1603 - 1867), is a region that produced many renowned samurai warriors such as Saigo Takamori and Okubo Toshimichi who contributed to the modernization of Japan.
                        The samurai of Satsuma ceaselessly honed “bugei” (loosely translated as “military arts”), including firearms and archery, even during the peaceful Edo period.
                        This unwavering perseverance ultimately helped to guide Japan's modernization following the Meiji Restoration.
                        The hardworking samurai were able to relax and unwind through “dareyame” (lit. “fatigue-stopper”), a custom involving an evening drink of Satsuma Shochu complemented with local cuisine. Samurai found solace in their daily lives by enjoying shochu with side dishes like “Tsukeage”— a deep-fried fish cake made by combining fish caught in Kagoshima Bay with local sake.
                        This tasty treat became known and loved throughout Japan as “Satsuma-age.”
                        Visitors to Satsuma today can enjoy savoring the local cuisine and admiring the samurai homes that have been carefully preserved to this day; allowing a glimpse of the human aspect of these fearless warriors.
                    </p>
                    <div class="btns">
                        <ul>
                            <li>
                                <router-link :to="{name: 'Popular', params: {id: 'd87f327a-98a1-45d2-82bf-9b57824b5410', page: 'about', url:'martial-towns-satsuma-samurai-kagoshima'}}" class="-black">
                                    <span>Read More</span>
                                </router-link>
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="img"><img src="../../assets/spirit_of_japan7/img02.webp" alt=""></div>
            </div>
        </section>
        <section class="basic-section">
            <div class="para -reverse">
                <div class="img"><img src="../../assets/spirit_of_japan7/img03.webp" alt=""></div>
                <div class="txt">
                    <h3>The Omiki Offering to the Gods</h3>
                    <p>
                        Mt. Takao has long been worshipped as a sacred mountain, significant for being where the high priest Gyoki established Yakuo-in Temple in 744 A.D.
                        In the 16th century, a powerful feudal lord called Hojo Ujiteru commissioned the construction of Hachioji Castle.
                        After its fall, the Shogun Tokugawa Ieyasu used Hachioji as a key stronghold west of Edo (present-day Tokyo) during the Edo period (1603-1867).
                        Hachioji prospered from its production of silk to the point where it took the name  “Soto,” or literally “Mulberry City”, soon growing into a bustling post town frequented by silk merchants traveling to and from Yokohama Port along the “Silk Road”.
                        Blessed with abundant rivers and natural subsoil springs, Hachioji is famous not only for its silk production, but also its sake culture.
                        Starting with “Takaosan” (lit. “Mt. Takao”), offered to the patron deity of Yakuo-in as an “omiki”, or sacred sake, many of the sake varieties brewed in Hachioji have been given names deeply intertwined with the region’s history.
                        These include “Kuwa-no-miyako” (lit. “Mulberry City”), “Hachioji-jo” (lit. “Hachioji Castle”), “Ujiteru” (a reference to historical figure Hojo Ujiteru), and “Takao no Tengu”—Tengu are supernatural beings often featured in Japanese folklore. To this day, these meticulously-brewed craft sake are enjoyed by locals and visitors alike.
                        To this day, these meticulously-brewed craft sake are enjoyed by locals and visitors alike.
                    </p>
                    <div class="btns">
                        <ul>
                            <li>
                                <router-link :to="{name: 'Popular', params: {id: '5f8d8270-67a5-4e47-b92d-cf590e3cf9bc', page: 'about', url: 'takao-mountain-mulberry-tokyo-hachioji-geisha'}}" class="-black">
                                    <span>Read More</span>
                                </router-link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>
        <section class="basic-section">
            <div class="para">
                <div class="img"><img src="../../assets/spirit_of_japan7/img04.webp" alt=""></div>
                <div class="txt">
                    <h3>"Itami Morohaku" and "Nada no Ki-ippon" ~ Kansai sakes beloved in Edo ~</h3>
                    <p>
                        During the Edo Period (1603-1867), sake brewed in the towns of Itami and Nada in Japan’s Kansai region (West Japan, including Osaka, Kyoto, and Hyogo) enjoyed immense popularity in the capital city of Edo (modern-day Tokyo).
                        Kyoto, where the Emperor resided, and its surrounding areas were referred to as “Kamigata,” (lit. “upper region”),  so sake sent to Edo was called “kudarizake” (lit. “downstream sake”).
                        For the people of Edo, who had cultivated a refined palate, the term kudarizake became synonymous with the highest-grade brand.
                        <br>
                        Blessed with the pristine waters of the Rokko Mountain Range dominating the sky in southeastern Hyogo Prefecture—in particular the moderately hard mineral-rich “Miyamizu” of Nishinomiya—the towns of this region firmly established their status as pioneers in sake brewing.
                        Groundbreaking rice milling using water wheels, outstanding skills of sake brewers, and specialized, high-speed boats known as “tarukaisen” ensuring the stable supply of sake set the standards for Japanese sake that continue to be upheld to this day.
                        Even now, Itami is home to Konishi Brewing Company, which has been brewing sake for over four centuries, as well as many more of Japan’s oldest breweries and sake merchant homes.
                        Nada-Gogo is an area comprising five main districts, all renowned for their sake traditions.
                        It spans approximately 12 kilometers from east to west and still firmly holds its status of Japan’s largest sake-producing region.
                        Visitors to Nada-Gogo can enjoy the impressive scenery of multiple, large traditional sake breweries all clustered together.
                    </p>
                    <div class="btns">
                        <ul>
                            <li>
                                <router-link :to="{name: 'Popular', params: {id: '85aa4088-7818-4ff3-9712-6be6a845671e', page: 'about', url: 'itami-morohaku-nada-no-ki-ippon-kansai-sakes-beloved-edo'}}" class="-black">
                                    <span>Read More</span>
                                </router-link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>
    <section class="section03">
            <div class="inner">
                <h2>Other Articles</h2>
                <ul>
                    <li>
                        <router-link :to="{name: 'SpiritOfJapan1'}">
                            <div class="img"><img src="../../assets/spirit_of_japan/other.jpg" alt=""></div>
                            <div class="text">
                                <div class="headline">Spirit of Japan</div>
                                <div class="title">Discover Craftsmanship Unbowed by Time</div>
                            </div>
                        </router-link>
                    </li>
                    <li>
                        <router-link :to="{name: 'SpiritOfJapan2'}">
                            <div class="img"><img src="../../assets/spirit_of_japan2/other.jpg" alt=""></div>
                            <div class="text">
                                <div class="headline">Spirit of Japan</div>
                                <div class="title">Sacred Sites where Nature and Faith Blend into One</div>
                            </div>
                        </router-link>
                    </li>
                    <li>
                        <router-link :to="{name: 'SpiritOfJapan3'}">
                            <div class="img"><img src="../../assets/spirit_of_japan3/other.png" alt=""></div>
                            <div class="text">
                                <div class="headline">Spirit of Japan</div>
                                <div class="title">Unlocking the Secrets of the Shogun, Samurai and Ninja</div>
                            </div>
                        </router-link>
                    </li>
                    <li>
                        <router-link :to="{name: 'SpiritOfJapan4'}">
                            <div class="img"><img src="../../assets/spirit_of_japan4/other.webp" alt=""></div>
                            <div class="text">
                                <div class="headline">Spirit of Japan</div>
                                <div class="title">The Journey of Japan's
                                    Original Landscapes Shaped by Water</div>
                            </div>
                        </router-link>
                    </li>
                    <li>
                        <router-link :to="{name: 'SpiritOfJapan5'}">
                            <div class="img"><img src="../../assets/spirit_of_japan5/other.webp" alt=""></div>
                            <div class="text">
                                <div class="headline">Spirit of Japan</div>
                                <div class="title">Encounter Festivals
                                    Embodying the Japanese Spirit</div>
                            </div>
                        </router-link>
                    </li>
                    <li>
                        <router-link :to="{name: 'SpiritOfJapan6'}">
                            <div class="img"><img src="../../assets/spirit_of_japan6/other.webp" alt=""></div>
                            <div class="text">
                                <div class="headline">Spirit of Japan</div>
                                <div class="title">Ancient Paths
                                    Where Japanese Culture Dwells</div>
                            </div>
                        </router-link>
                    </li>
                </ul>
            </div>
        </section>
    </div>
</template>

<script>
import ApiRouter from "../../components/Systems/ApiRouter";

export default {
    name: "PlanYourVisit",
    mixins: [ApiRouter],
    data: function () {
        return {
            articles: [],
            activeTabMenu: 'half'
        }
    },
    created: function () {
        this.loadData();
        this.getQuery();
        document.title = 'Spirit Of Japan | Japan Heritage Official Site'
    },
    methods: {
        loadData: function () {
            window.axios.get(this.routes.articles)
                .then((response) => {
                    this.articles = response.data.data;
                });
        },
        getQuery: function () {
            const tab = this.$route.query.tab;

            switch (tab) {
                case 'day1':
                    this.activeTabMenu = 'day1';
                    break;
                case 'day2':
                    this.activeTabMenu = 'day2';
                    break;
                case 'day3':
                    this.activeTabMenu = 'day3';
                    break;
                default:
                    this.activeTabMenu = 'half';
                    break;
            }
        },
        articleCardHasClicked: function (id, hash, breadcrumb) {
            this.$router.push({name: 'Popular', params: {id: id, page: 'travelInfo', breadcrumb: breadcrumb}, hash: hash});
        }
    },
    watch: {
        $route() {
            this.getQuery();
        }
    }
}
</script>

<style src="./SpiritOfJapan7.scss" lang="scss" scoped />
